import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Modal,
  TextField,
  IconButton,
  InputAdornment,
  Backdrop,
  Switch,
  FormControlLabel
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  BCPUsersViewfinder,
  BCPSquarePlus,
  BCPXmark,
  BCPUserMagnifyingGlass,
  BCPShop
} from "../../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData
} from "../../../../components/utilities/UtilityFunctions";
import { postNotification } from "../../../../components/utilities/Notifications";
import { handlePagingAll } from "../../../../components/api/APIUtilities";
import { Formik } from "formik";

const NoOrganizationCard = ({ theme }) => (
  <Card
    sx={{
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      textAlign: "center",
      p: 4
    }}
  >
    <BCPShop size={48} style={{ marginBottom: "16px", opacity: 0.7 }} />
    <Typography variant="h5" gutterBottom>
      No Organization Access
    </Typography>
    <Typography variant="body1" color="text.secondary" paragraph>
      You currently don't have access to any organization. To manage token
      whitelists, you need to either create your own organization or join an
      existing one.
    </Typography>
    <Box sx={{ mt: 3 }}>
      <Button
        component={RouterLink}
        to="/organize/organization"
        variant="contained"
        color="primary"
        startIcon={<BCPSquarePlus />}
        sx={{ minWidth: 200 }}
      >
        Create Organization
      </Button>
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
      Need access to an existing organization? Contact the organization owner.
    </Typography>
  </Card>
);

export default function OrgDashWhitelist(props) {
  const { organization, authContext, organize } = props;
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [whitelistRecords, setWhitelistRecords] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(organization?.id || null);
  const [whitelistModal, setWhitelistModal] = useState(null);
  const [processing, setProcessing] = useState(false);

  const fetchOrganizations = async () => {
    try {
      const apiGameStores = await handlePagingAll(`/gamestores`, {
        myStores: true,
        sortAscending: false,
        "expand[]": ["location"]
      });
      setOrganizations(apiGameStores || []);

      if (!selectedOrgId && apiGameStores?.length > 0) {
        setSelectedOrgId(apiGameStores[0].id);
      }
    } catch (err) {
      postNotification("error", err);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWhitelistRecords = async (orgId) => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const apiWhitelistRecords = await getData(
        `/gamestores/${orgId}/whitelistrecords`
      );

      const records = Array.isArray(apiWhitelistRecords)
        ? apiWhitelistRecords
        : apiWhitelistRecords.data || [];

      setWhitelistRecords(records);
    } catch (err) {
      postNotification("error", err);
      setWhitelistRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const searchUser = async (email) => {
    if (!email || email.trim() === "") return null;

    try {
      setProcessing(true);
      const apiUser = await getData(`/users/${email.trim().toLowerCase()}`, {
        queryStringParameters: {}
      });
      return apiUser;
    } catch (err) {
      return null;
    } finally {
      setProcessing(false);
    }
  };

  const createWhitelistRecord = async (userId, orgId) => {
    try {
      setProcessing(true);
      await postData(`/gamestores/${orgId}/whitelistrecords`, {
        userId: userId
      });

      await fetchWhitelistRecords(orgId);
      postNotification("success", "User added to whitelist successfully");
      setWhitelistModal(null);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setProcessing(false);
    }
  };

  const toggleActiveStatus = async (recordId, currentStatus, orgId) => {
    try {
      setProcessing(true);
      const newActiveStatus = !currentStatus;

      await postData(
        `/gamestores/${orgId}/whitelistrecords/${recordId}/setactive`,
        {
          active: newActiveStatus
        }
      );

      setWhitelistRecords((prevRecords) =>
        prevRecords.map((record) =>
          record.id === recordId
            ? { ...record, active: newActiveStatus }
            : record
        )
      );

      postNotification(
        "success",
        `Record ${newActiveStatus ? "activated" : "deactivated"} successfully`
      );
    } catch (err) {
      postNotification("error", err);
      await fetchWhitelistRecords(orgId);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (organize && authContext?.authState?.identity?.userId) {
      fetchOrganizations();
    } else if (organization?.id) {
      setSelectedOrgId(organization.id);
      fetchWhitelistRecords(organization.id);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [organization, authContext, organize]);

  useEffect(() => {
    if (selectedOrgId) {
      fetchWhitelistRecords(selectedOrgId);
    }
    // eslint-disable-next-line
  }, [selectedOrgId]);

  if (!selectedOrgId && !organize && !loading) {
    return <NoOrganizationCard theme={theme} />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const selectedOrg =
    organizations.find((org) => org.id === selectedOrgId) || organization;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BCPUsersViewfinder size={24} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Token White List
          </Typography>
        </Box>

        {selectedOrgId && (
          <Button
            variant="contained"
            onClick={() => setWhitelistModal({})}
            startIcon={<BCPSquarePlus />}
          >
            New Token Whitelist
          </Button>
        )}
      </Box>

      {organize && organizations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Organization</InputLabel>
            <Select
              value={selectedOrgId || ""}
              label="Select Organization"
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {organize && organizations.length === 0 && !loading ? (
        <NoOrganizationCard theme={theme} />
      ) : whitelistRecords.length === 0 ? (
        <Card
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <CardContent
            sx={{
              textAlign: "center",
              py: 4
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No whitelist records found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedOrg?.name
                ? `There are currently no users on the token whitelist for ${selectedOrg.name}.`
                : "There are currently no users on the token whitelist for this organization."}
            </Typography>
            {selectedOrgId && (
              <Button
                variant="contained"
                onClick={() => setWhitelistModal({})}
                startIcon={<BCPSquarePlus />}
                sx={{ mt: 2 }}
              >
                Add First User
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {whitelistRecords.map((record, index) => (
            <Grid item xs={12} sm={6} md={4} key={record.id || index}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "box-shadow 0.2s ease",
                  opacity: record.active ? 1 : 0.6,
                  backgroundColor: record.active
                    ? theme.palette.background.paper
                    : theme.palette.action.disabledBackground,
                  "&:hover": {
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: record.active
                            ? theme.palette.text.primary
                            : theme.palette.text.disabled
                        }}
                      >
                        {record.user?.firstName && record.user?.lastName
                          ? `${record.user.firstName} ${record.user.lastName}`
                          : record.user?.firstName ||
                            record.user?.lastName ||
                            record.userName ||
                            "Unknown User"}
                      </Typography>

                      <Chip
                        label={record.active ? "Active" : "Inactive"}
                        size="small"
                        color={record.active ? "success" : "default"}
                        sx={{
                          fontWeight: 500,
                          minWidth: "60px"
                        }}
                      />
                    </Box>

                    {(record.circuit?.name || record.circuitName) && (
                      <Chip
                        label={record.circuit?.name || record.circuitName}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.secondary.main,
                          color: theme.palette.secondary.contrastText,
                          fontWeight: 500,
                          alignSelf: "flex-start",
                          opacity: record.active ? 1 : 0.7
                        }}
                      />
                    )}

                    {record.gameStore?.name && (
                      <Chip
                        label={record.gameStore.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 500,
                          alignSelf: "flex-start",
                          opacity: record.active ? 1 : 0.7
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                        pt: 1,
                        borderTop: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={record.active}
                            onChange={() =>
                              toggleActiveStatus(
                                record.id,
                                record.active,
                                selectedOrgId
                              )
                            }
                            disabled={processing}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {record.active ? "Active" : "Inactive"}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* New Whitelist Modal */}
      <Modal
        disableAutoFocus
        open={Boolean(whitelistModal)}
        onClose={() => setWhitelistModal(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
        sx={{ zIndex: 990 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 500],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => setWhitelistModal(null)}
          >
            <BCPXmark />
          </IconButton>

          <Grid
            container
            spacing={1}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                Add User to Whitelist
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedOrg?.name &&
                  `Adding user to ${selectedOrg.name} whitelist`}
              </Typography>
            </Grid>
          </Grid>

          <Formik
            initialValues={{
              email: "",
              firstName: "",
              lastName: "",
              userId: null
            }}
            onSubmit={async (values) => {
              if (!values.userId) {
                postNotification(
                  "error",
                  "Please search for and select a valid user"
                );
                return;
              }

              // Check if user is already in whitelist
              const existingRecord = whitelistRecords.find(
                (record) =>
                  record.user?.id === values.userId ||
                  record.userId === values.userId
              );

              if (existingRecord) {
                postNotification("error", "User is already on the whitelist");
                return;
              }

              await createWhitelistRecord(values.userId, selectedOrgId);
            }}
          >
            {({
              errors,
              handleBlur,
              handleSubmit,
              touched,
              values,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="email"
                      value={values.email}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Search"
                              edge="end"
                              color="primary"
                              disabled={processing}
                              onClick={async () => {
                                if (!values.email.trim()) {
                                  postNotification(
                                    "error",
                                    "Please enter an email address"
                                  );
                                  return;
                                }

                                const user = await searchUser(values.email);
                                if (user) {
                                  setFieldValue("userId", user.id);
                                  setFieldValue(
                                    "firstName",
                                    user.firstName || ""
                                  );
                                  setFieldValue(
                                    "lastName",
                                    user.lastName || ""
                                  );
                                  postNotification("success", "User found");
                                } else {
                                  setFieldValue("userId", null);
                                  setFieldValue("firstName", "");
                                  setFieldValue("lastName", "");
                                  postNotification("error", "User not found");
                                }
                              }}
                            >
                              <BCPUserMagnifyingGlass />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      onBlur={async (e) => {
                        handleBlur(e);
                        if (e.target.value.trim()) {
                          const user = await searchUser(e.target.value);
                          if (user) {
                            setFieldValue("userId", user.id);
                            setFieldValue("firstName", user.firstName || "");
                            setFieldValue("lastName", user.lastName || "");
                          } else {
                            setFieldValue("userId", null);
                            setFieldValue("firstName", "");
                            setFieldValue("lastName", "");
                          }
                        }
                      }}
                      onChange={(e) => {
                        const trimmedString = e.target.value
                          .trim()
                          .toLowerCase();
                        setFieldValue("email", trimmedString);
                      }}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="First Name"
                      value={values.firstName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Name"
                      value={values.lastName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      fullWidth
                      variant="outlined"
                      onClick={() => setWhitelistModal(null)}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      color="success"
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={processing || !values.userId}
                    >
                      {processing ? "Adding..." : "Add to Whitelist"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
    </Box>
  );
}
